import { PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DeleteConfirmPopoverContent } from '@/components/atoms/DeleteConfirmPopover/DeleteConfirmPopoverContent';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor } from '@/components/ui/popover';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { Macro } from '@/shared/types/macros';

import MacroRowLabel from './MacroRowLabel';

interface Props {
  macro: Macro;
  isDeleteOpen: boolean;
  onSelect: () => void;
  canWrite: boolean;
  onEdit: () => void;
  onRequestDelete: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
}

const SavedResponseRow = ({
  macro,
  isDeleteOpen,
  onSelect,
  canWrite,
  onEdit,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
}: Props) => {
  const { t } = useTranslation();
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLSpanElement>('vertical', [macro.text]);

  return (
    <div className="group flex items-center rounded hover:bg-highlight-background">
      <InfoTooltip
        variant="rich"
        size="auto"
        side="right"
        content={
          isTruncated ? (
            <div className="flex max-w-64 flex-col">
              <div className="px-3 py-2">
                <p className="text-xs leading-4 font-semibold">{macro.name}</p>
              </div>
              <div className="border-t-[0.5px] border-t-stroke-muted-40 px-3 pt-2.5 pb-3">
                <p className="text-xs leading-5 text-text-primary-foreground/80">{macro.text}</p>
              </div>
            </div>
          ) : undefined
        }
      >
        <button
          className="flex min-w-0 flex-1 flex-col gap-1 px-2 py-1.5 text-left"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onSelect}
        >
          <MacroRowLabel name={macro.name} text={macro.text} textRef={textRef} />
        </button>
      </InfoTooltip>

      <Popover open={isDeleteOpen}>
        <div
          className="flex shrink-0 items-center gap-1 pr-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100"
          hidden={!canWrite}
        >
          <PopoverAnchor asChild>
            <Button
              type="button"
              variant="ghost-neutral"
              size="icon-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onRequestDelete}
              aria-label={t('chatBox.modal.delete_response')}
              className="hover:[&_svg]:text-destructive"
            >
              <Trash2Icon size={14} />
            </Button>
          </PopoverAnchor>
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onEdit}
            aria-label={t('chatBox.modal.edit_quick_response')}
          >
            <PencilIcon size={14} />
          </Button>
        </div>
        <DeleteConfirmPopoverContent
          title={t('chatBox.modal.delete_saved_response_title')}
          description={t('chatBox.modal.delete_saved_response_subtitle', { 0: macro.name })}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
          onPointerDownOutside={onCancelDelete}
          onEscapeKeyDown={onCancelDelete}
        />
      </Popover>
    </div>
  );
};

export default SavedResponseRow;
